import React, { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  MessageSquare,
  LayoutDashboard,
  Settings,
  CreditCard,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useIsMobile } from "@/hooks/use-mobile";

interface DashboardLayoutProps {
  children: React.ReactNode;
  title: string;
  activePath: string;
}

const navItems = [
  {
    path: "/dashboard",
    label: "Dashboard",
    icon: <LayoutDashboard className="h-5 w-5" />,
  },
  // {
  // 	path: '/dashboard/emails',
  // 	label: 'Emails',
  // 	icon: <Mail className="h-5 w-5" />,
  // },
  {
    path: "/settings",
    label: "Settings",
    icon: <Settings className="h-5 w-5" />,
  },
  {
    path: "/settings/subscription",
    label: "Subscription",
    icon: <CreditCard className="h-5 w-5" />,
  },
];

export function DashboardLayout({
  children,
  title,
  activePath,
}: DashboardLayoutProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Get user's initials for avatar
  const userInitials =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
          .split(" ")
          .map((part: string) => part[0])
          .join("")
      : "U";

  const handleLogout = () => {
    logout();
    navigate({ to: "/" });
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Mobile Sidebar Backdrop */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar Navigation */}
      <div
        className={`${
          isMobile
            ? `fixed inset-y-0 left-0 z-30 w-64 transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`
            : "w-64"
        } border-r bg-sidebar p-4 flex flex-col transition-transform duration-300 ease-in-out`}
      >
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center">
            <MessageSquare className="h-6 w-6 text-sidebar-primary mr-2" />
            <h1 className="text-2xl font-bold text-sidebar-foreground">
              aiEmailAgent<span className="text-blue-500">.</span>
            </h1>
          </div>
          {isMobile && (
            <button
              onClick={toggleSidebar}
              className="text-sidebar-foreground p-1"
              aria-label="Close sidebar"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <nav className="space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => isMobile && setSidebarOpen(false)}
              className={`flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                activePath === item.path
                  ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
                  : "text-sidebar-foreground hover:bg-sidebar-accent/50"
              }`}
            >
              <span
                className={`mr-3 ${activePath === item.path ? "text-sidebar-primary-foreground" : "text-sidebar-foreground opacity-70"}`}
              >
                {item.icon}
              </span>
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="mt-auto pt-6">
          <button
            onClick={handleLogout}
            className="flex w-full items-center rounded-lg px-3 py-2.5 text-sm font-medium text-sidebar-foreground hover:bg-sidebar-accent/50 transition-all duration-200"
          >
            <LogOut className="h-5 w-5 mr-3 opacity-70" />
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="border-b p-4 bg-card shadow-sm">
          <div className="flex items-center justify-between">
            {isMobile && (
              <button
                onClick={toggleSidebar}
                className="mr-3 p-1 rounded hover:bg-gray-100"
                aria-label="Open sidebar"
              >
                <Menu className="h-6 w-6" />
              </button>
            )}
            <h2 className="text-xl font-semibold text-foreground truncate">
              {title}
            </h2>

            <div className="flex items-center space-x-2 sm:space-x-4 ml-auto">
              <span className="text-sm font-medium text-foreground hidden sm:inline">
                {user?.firstName
                  ? `${user.firstName} ${user.lastName}`
                  : "User"}
              </span>
              <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-800 font-medium text-sm border border-blue-200 shadow-sm">
                {userInitials}
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto bg-background">
          <div className="animate-fade-in">{children}</div>
        </div>
      </div>
    </div>
  );
}
